import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { toast } from 'react-toastify';
import { financeAPI } from '../services/api';

const FinancePage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  const fetchReport = () => {
    setLoading(true);
    financeAPI.getReport(filters)
      .then((res) => setReport(res.data.data))
      .catch(() => toast.error('فشل في تحميل التقرير'))
      .finally(() => setLoading(false));
  };

  const chartData = report?.trips?.map((t) => ({
    date: t.date,
    إيرادات: t.revenue,
    تكاليف: Object.values(t.costs || {}).reduce((a, b) => a + parseFloat(b || 0), 0),
  })) || [];

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6">
      <h1 className="text-xl md:text-2xl font-bold text-gray-800">التقارير المالية</h1>

      <div className="bg-white rounded-xl shadow p-5">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="label">من تاريخ</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
              className="input-field"
            />
          </div>
          <div>
            <label className="label">إلى تاريخ</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
              className="input-field"
            />
          </div>
          <button onClick={fetchReport} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg">
            {loading ? 'جاري التحميل...' : 'عرض التقرير'}
          </button>
        </div>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: 'إجمالي الحجوزات', value: report.summary.totalBookings, color: 'text-blue-700' },
              { label: 'إجمالي الإيرادات', value: `${report.summary.totalRevenue.toLocaleString()} جنيه`, color: 'text-green-700' },
              { label: 'إجمالي التكاليف', value: `${report.summary.totalCosts.toLocaleString()} جنيه`, color: 'text-red-600' },
              { label: 'صافي الربح', value: `${report.summary.netProfit.toLocaleString()} جنيه`, color: report.summary.netProfit >= 0 ? 'text-green-700' : 'text-red-600' },
              { label: '💰 عمولة المنصة', value: `${(report.summary.totalCommission || 0).toLocaleString()} جنيه`, color: 'text-orange-600' },
              { label: '🏦 صافي إيراد الشركة', value: `${(report.summary.netRevenue || 0).toLocaleString()} جنيه`, color: 'text-teal-700' },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl shadow p-5 text-right">
                <p className="text-gray-500 text-sm">{s.label}</p>
                <p className={`text-xl font-bold mt-1 ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Commission breakdown */}
          {(report.summary.totalCommission || 0) > 0 && (
            <div className="bg-white rounded-xl shadow p-5">
              <h2 className="text-base font-semibold text-gray-700 mb-3">تفصيل عمولة المنصة</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-right">
                  <p className="text-gray-500 text-sm">عمولة الحجوزات الإلكترونية</p>
                  <p className="text-blue-700 font-bold text-lg mt-1">{(report.summary.onlineCommission || 0).toLocaleString()} ج</p>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 text-right">
                  <p className="text-gray-500 text-sm">عمولة حجوزات المكتب</p>
                  <p className="text-orange-700 font-bold text-lg mt-1">{(report.summary.officeCommission || 0).toLocaleString()} ج</p>
                </div>
                <div className="bg-teal-50 rounded-xl p-4 text-right">
                  <p className="text-gray-500 text-sm">صافي ما تحتفظ به الشركة</p>
                  <p className="text-teal-700 font-bold text-lg mt-1">{(report.summary.netRevenue || 0).toLocaleString()} ج</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">الإيرادات vs التكاليف</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="إيرادات" stroke="#22c55e" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="تكاليف" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="p-4 border-b"><h2 className="font-semibold text-gray-700">تفاصيل الرحلات</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-right">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3">المسار</th>
                    <th className="px-4 py-3">التاريخ</th>
                    <th className="px-4 py-3">المقاعد المحجوزة</th>
                    <th className="px-4 py-3">الإيرادات</th>
                    <th className="px-4 py-3">التكاليف</th>
                    <th className="px-4 py-3">صافي الربح</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {report.trips.map((t, i) => {
                    const costs = Object.values(t.costs || {}).reduce((a, b) => a + parseFloat(b || 0), 0);
                    const profit = t.revenue - costs;
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{t.route}</td>
                        <td className="px-4 py-3 text-gray-500">{t.date}</td>
                        <td className="px-4 py-3">{t.bookedSeats} / {t.totalSeats}</td>
                        <td className="px-4 py-3 text-green-600">{t.revenue.toLocaleString()} ج</td>
                        <td className="px-4 py-3 text-red-500">{costs.toLocaleString()} ج</td>
                        <td className={`px-4 py-3 font-semibold ${profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                          {profit.toLocaleString()} ج
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FinancePage;
